// Turns FlightHub's Trip Details shape (see
// ~/Desktop/afritrip-oui-designer for the mini-app's equivalent, and
// abdel_koumare-attachments (2)/openapi-trip-details-flighthub.yaml for the
// spec) into the simple display shapes the booking page and the PDF use.

/** FlightHub local times are "YYYY-MM-DD HH:MM:SS", no timezone offset. */
import { currentLang, tr } from './i18n';

export function formatDate(localDateTime) {
  if (!localDateTime) return '';
  const [datePart] = localDateTime.split(' ');
  const d = new Date(`${datePart}T00:00:00`);
  return d.toLocaleDateString(currentLang() === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatTime(localDateTime) {
  if (!localDateTime) return '';
  const [, timePart] = localDateTime.split(' ');
  if (!timePart) return '';
  const [h, m] = timePart.split(':');
  if (currentLang() === 'fr') return `${h.padStart(2, '0')}:${m}`;
  const hour = Number(h);
  return `${hour % 12 === 0 ? 12 : hour % 12}:${m}${hour >= 12 ? 'PM' : 'AM'}`;
}

export function formatDuration(seconds) {
  if (seconds == null) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return currentLang() === 'fr' ? `${h} h ${String(m).padStart(2, '0')} min` : `${h}h ${m}m`;
}

export function cabinLabel(raw) {
  if (!raw) return '';
  const key = String(raw).toLowerCase().replace(/[\s-]+/g, '_');
  return tr('cabins')[key] || raw[0].toUpperCase() + raw.slice(1);
}

export function carrierName(code, carriers) {
  return carriers?.find((c) => c.code === code)?.name || code || '';
}

/**
 * Seat(s) bought for the given segments, from FlightHub's ancillaries of type
 * "seat". One passenger: "10A". Several: "Jane Doe 10A, John Doe 10B".
 */
function seatsForSegments(booking, segmentIds) {
  const seats = (booking?.ancillaries ?? []).filter(
    (a) => a.type === 'seat' && a.serviceValue && segmentIds.includes(a.segmentId)
  );
  if (!seats.length) return '';
  const names = new Map((booking?.passengers ?? []).map((p) => [p.id, p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ')]));
  const many = (booking?.passengers ?? []).length > 1;
  return seats.map((a) => (many ? `${names.get(a.passengerId) || ''} ${a.serviceValue}`.trim() : a.serviceValue)).join(', ');
}

/**
 * One row per city_pair (leg), with the airline/cabin/stops resolved from
 * its segments. A leg's own `duration` already includes any connection
 * time, per the spec — departure/arrival come from the first/last segment.
 */
/** "Bamako, Mali" for an airport code, from the airports list of Trip Details. */
export function placeLabel(code, airports) {
  const a = airports?.find((x) => x.code === code);
  if (!a) return '';
  return [a.city || a.name, a.country_name].filter(Boolean).join(', ');
}

/** What the customer paid, and how much of it was seats. */
export function buildPrice(booking, transactionSummary) {
  // The invoice on the booking is what FlightHub billed. The Transaction Summary
  // counts seat fees once per segment on connecting flights, so it is only a fallback.
  const invoiced = booking?.billingInformation?.summary?.customerAmount;
  const total = invoiced != null ? Math.round(Number(invoiced)) : transactionSummary?.total;
  const currency = booking?.billingInformation?.summary?.currency;
  const items = (booking?.invoices ?? []).flatMap((i) => i.invoice_items ?? []);
  const fromInvoice = items.filter((i) => i.product_type === 'seat_fees').reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const raw = items.length ? fromInvoice : transactionSummary?.items?.seat_fees?.total;
  // Only show a seat part when the booking really has a seat.
  const hasSeat = (booking?.ancillaries ?? []).some((a) => a.type === 'seat');
  const seatFees = hasSeat && Number(raw) > 0 ? Math.round(Number(raw)) : 0;
  return { total, currency, seatFees };
}

export function buildLegs(booking, carriers, airports) {
  const cityPairs = booking?.itinerary?.city_pairs ?? [];
  const segmentsById = new Map((booking?.segments ?? []).map((s) => [s.id, s]));

  return cityPairs.map((pair, index) => {
    const segs = (pair.segments ?? []).map((id) => segmentsById.get(id)).filter(Boolean);
    const first = segs[0];
    const stops = segs.length - 1;
    const legSeats = seatsForSegments(booking, pair.segments ?? []);
    return {
      seatsText: legSeats,
      label: index === 0 ? tr('depart') : index === cityPairs.length - 1 ? tr('ret') : tr('flightN', index + 1),
      dateLabel: formatDate(pair.departure_time),
      from: pair.departure_airport_code,
      to: pair.arrival_airport_code,
      fromPlace: placeLabel(pair.departure_airport_code, airports),
      toPlace: placeLabel(pair.arrival_airport_code, airports),
      departureTime: formatTime(pair.departure_time),
      arrivalTime: formatTime(pair.arrival_time),
      duration: formatDuration(pair.duration),
      stopsLabel: stops === 0 ? tr('direct') : stops === 1 ? tr('oneStop') : tr('stops', stops),
      cabin: cabinLabel(first?.cabinClass),
      // The airline's own record locator (what the airline calls the PNR)
      pnr: first?.control_number || '',
      airline: carrierName(first?.marketing_carrier_code, carriers)
    };
  });
}

/** One row per passenger, with their e-ticket number if issued. */
export function buildPassengers(booking) {
  return (booking?.passengers ?? []).map((p) => {
    const ticket = (p.documents ?? []).find((d) => d.type === 'eticket');
    return {
      name: p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' '),
      dob: p.date_of_birth || '',
      eTicket: ticket?.number || ''
    };
  });
}

export function formatMoney(amount, currency) {
  if (amount == null) return '';
  // fr-FR separates thousands with a narrow no-break space, which the PDF font cannot draw — use a plain space.
  const n = Number(amount)
    .toLocaleString(currentLang() === 'fr' ? 'fr-FR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    .replace(/[\u202f\u00a0]/g, ' ');
  return currency ? `${n} ${currency}` : n;
}

// ---- baggage + fare conditions ------------------------------------------------

const STATUS = { inc: 'inc', cha: 'cha', nof: 'nof' };
const statusOf = (rule) => STATUS[String(rule?.offer_type || '').toLowerCase()] || null;

/** Checked allowance as the airline recorded it on a flight: pieces (N) or weight (W). */
function segmentAllowance(seg) {
  const a = seg?.baggageAllowance;
  if (!a || !a.quantity) return '';
  if (a.type === 'W') return String(a.unit || '').toUpperCase().startsWith('L') ? tr('bagLb', a.quantity) : tr('bagKg', a.quantity);
  return tr('bagPieces', a.quantity);
}

/**
 * One entry per leg: its checked allowance from the segments, plus the four
 * baggage rules from the fare rules. A rule that is absent is left null, never
 * guessed (the FlightHub spec says not to infer anything from an absent rule).
 * Returns null when there is nothing at all to show.
 */
export function buildBaggage(booking, fareRules) {
  const cityPairs = booking?.itinerary?.city_pairs ?? [];
  const segmentsById = new Map((booking?.segments ?? []).map((s) => [s.id, s]));
  const flightRules = fareRules?.flight_rules ?? [];
  const same = fareRules?.flights_have_same_rules;
  const rows = cityPairs.map((pair, i) => {
    const segs = (pair.segments ?? []).map((id) => segmentsById.get(id)).filter(Boolean);
    const allowance = [...new Set(segs.map(segmentAllowance).filter(Boolean))].join(' / ');
    const rules = (flightRules[i] ?? (same ? flightRules[0] : null))?.baggage_allowance ?? {};
    return {
      route: `${pair.departure_airport_code} to ${pair.arrival_airport_code}`.replace(' to ', ' → '),
      allowance,
      personal: statusOf(rules.personal_item),
      cabin: statusOf(rules.carry_on),
      checked: statusOf(rules.first_checked_bag),
      checked2: statusOf(rules.second_checked_bag)
    };
  });
  const any = rows.some((r) => r.allowance || r.personal || r.cabin || r.checked || r.checked2);
  return { rows, any };
}

/** Refund / exchange / void conditions, only the ones FlightHub actually sent. */
export function buildConditions(fareRules) {
  const rules = fareRules?.ticket_rules ?? {};
  const names = tr('cond');
  return Object.keys(rules)
    .map((key) => ({
      label: names[key] || key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
      status: statusOf(rules[key])
    }))
    .filter((c) => c.status);
}

export const baggageStatusLabel = (st) => (st ? tr({ inc: 'bagInc', cha: 'bagCha', nof: 'bagNof' }[st]) : '');
export const conditionStatusLabel = (st) => (st ? tr({ inc: 'condInc', cha: 'condCha', nof: 'condNof' }[st]) : '');
